import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { tripsAPI, vehiclesAPI, subscriptionAPI, aiAPI, weatherAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, MapPin, Calendar, Truck, Loader2, Pencil, Trash2,
  CheckSquare, Crown, Route, X, Sparkles, Printer, ClipboardList,
  Cloud, Sun, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog, CloudSun, Wind, Droplets, Thermometer,
  AlertTriangle, ShieldAlert, Flame, Snowflake, Eye,
} from "lucide-react";

const WEATHER_ICONS = {
  sun: Sun, "cloud-sun": CloudSun, cloud: Cloud, "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle, "cloud-rain": CloudRain, "cloud-rain-wind": CloudRain,
  snowflake: CloudSnow, "cloud-lightning": CloudLightning,
};

function WeatherIcon({ icon, className }) {
  const Icon = WEATHER_ICONS[icon] || Cloud;
  return <Icon className={className} />;
}

const ALERT_ICONS = {
  thunderstorm: CloudLightning, flood: CloudRain, wind: Wind, snow: Snowflake,
  freeze: Thermometer, heat: Flame, visibility: Eye,
};

function AlertIcon({ type, className }) {
  const Icon = ALERT_ICONS[type] || AlertTriangle;
  return <Icon className={className} />;
}

export default function TripPlanner() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [gearChecklist, setGearChecklist] = useState(null);
  const [generatingChecklist, setGeneratingChecklist] = useState(null);
  const [weatherData, setWeatherData] = useState({});
  const [loadingWeather, setLoadingWeather] = useState({});
  const [weatherDetail, setWeatherDetail] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tripsRes, vRes, subRes] = await Promise.all([
        tripsAPI.list(),
        vehiclesAPI.list(),
        subscriptionAPI.status(),
      ]);
      setTrips(tripsRes.data);
      setVehicles(vRes.data);
      setIsPro(subRes.data.is_pro);
      // Auto-fetch weather for trips with locations
      for (const trip of tripsRes.data) {
        if (trip.location && trip.status !== "completed") {
          fetchWeather(trip.id);
        }
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchWeather = async (tripId) => {
    setLoadingWeather((prev) => ({ ...prev, [tripId]: true }));
    try {
      const { data } = await weatherAPI.tripForecast(tripId);
      setWeatherData((prev) => ({ ...prev, [tripId]: data }));
    } catch (err) {
      console.error("Weather fetch failed:", err);
    } finally {
      setLoadingWeather((prev) => ({ ...prev, [tripId]: false }));
    }
  };

  const handleCreate = async () => {
    try {
      const res = await tripsAPI.create({ name: "New Trip" });
      setTrips([res.data, ...trips]);
      setEditTrip(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create trip");
    }
  };

  const handleSave = async () => {
    if (!editTrip) return;
    try {
      const res = await tripsAPI.update(editTrip.id, {
        name: editTrip.name,
        trail_name: editTrip.trail_name,
        trip_date: editTrip.trip_date,
        location: editTrip.location,
        notes: editTrip.notes,
        vehicle_ids: editTrip.vehicle_ids,
        checklist: editTrip.checklist,
        status: editTrip.status,
      });
      setTrips(trips.map((t) => (t.id === editTrip.id ? res.data : t)));
      setEditTrip(null);
      toast.success("Trip saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    try {
      await tripsAPI.delete(id);
      setTrips(trips.filter((t) => t.id !== id));
      setDeleteConfirm(null);
      toast.success("Trip deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleVehicle = (vid) => {
    if (!editTrip) return;
    const ids = editTrip.vehicle_ids || [];
    setEditTrip({
      ...editTrip,
      vehicle_ids: ids.includes(vid) ? ids.filter((v) => v !== vid) : [...ids, vid],
    });
  };

  const addChecklistItem = () => {
    if (!editTrip) return;
    setEditTrip({
      ...editTrip,
      checklist: [...(editTrip.checklist || []), { text: "", done: false, _key: Date.now().toString(36) + Math.random().toString(36).slice(2, 6) }],
    });
  };

  const updateChecklistItem = (index, field, value) => {
    if (!editTrip) return;
    const list = [...(editTrip.checklist || [])];
    list[index] = { ...list[index], [field]: value };
    setEditTrip({ ...editTrip, checklist: list });
  };

  const removeChecklistItem = (index) => {
    if (!editTrip) return;
    setEditTrip({
      ...editTrip,
      checklist: (editTrip.checklist || []).filter((_, i) => i !== index),
    });
  };

  const handleGenerateChecklist = async (tripId) => {
    setGeneratingChecklist(tripId);
    try {
      const { data } = await aiAPI.gearChecklist(tripId);
      setGearChecklist(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate checklist");
    } finally {
      setGeneratingChecklist(null);
    }
  };

  const handlePrintChecklist = () => {
    if (!gearChecklist) return;
    const cats = gearChecklist.checklist?.categories || [];
    const escapeHtml = (str) => str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Gear Checklist</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 700px; margin: 0 auto; padding: 24px; color: #1a1a1a; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      .meta { font-size: 13px; color: #666; margin-bottom: 20px; }
      h2 { font-size: 15px; margin: 16px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #ddd; }
      .item { display: flex; align-items: center; gap: 8px; padding: 3px 0; font-size: 14px; }
      .checkbox { width: 14px; height: 14px; border: 1.5px solid #999; border-radius: 2px; flex-shrink: 0; }
      @media print { body { padding: 0; } }
    </style></head><body>
    <h1>${escapeHtml(gearChecklist.trip_name)} — Gear Checklist</h1>
    <div class="meta">${[gearChecklist.trail_name, gearChecklist.location, gearChecklist.trip_date].filter(Boolean).map(escapeHtml).join(" · ")}</div>
    ${cats.map(c => `<h2>${escapeHtml(c.name)}</h2>${(c.items || []).map(item => `<div class="item"><div class="checkbox"></div>${escapeHtml(item)}</div>`).join("")}`).join("")}
    <script>window.print();<\/script></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWin = window.open(url, "_blank");
    if (printWin) {
      printWin.onafterprint = () => URL.revokeObjectURL(url);
    }
  };

  const handleAddChecklistToTrip = async () => {
    if (!gearChecklist) return;
    const tripId = gearChecklist.trip_id;
    const trip = trips.find((t) => t.id === tripId);
    if (!trip) return;

    const existingTexts = new Set((trip.checklist || []).map((c) => c.text.toLowerCase()));
    const newItems = [];
    for (const cat of gearChecklist.checklist?.categories || []) {
      for (const item of cat.items || []) {
        if (!existingTexts.has(item.toLowerCase())) {
          newItems.push({ text: `[${cat.name}] ${item}`, done: false });
        }
      }
    }

    if (newItems.length === 0) {
      toast.info("All items already in your checklist");
      return;
    }

    try {
      const merged = [...(trip.checklist || []), ...newItems];
      await tripsAPI.update(tripId, { ...trip, checklist: merged });
      setTrips(trips.map((t) => t.id === tripId ? { ...t, checklist: merged } : t));
      toast.success(`Added ${newItems.length} items to trip checklist`);
    } catch {
      toast.error("Failed to update trip");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <Crown className="w-12 h-12 text-amber-500" />
        <h2 className="text-xl font-bold">Pro Feature</h2>
        <p className="text-muted-foreground text-center">Trip planning requires a Pro subscription.</p>
        <Button onClick={() => navigate("/subscription")}>Upgrade to Pro</Button>
      </div>
    );
  }

  const statusColor = (s) => ({
    planned: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    active: "bg-green-500/20 text-green-400 border-green-500/30",
    completed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  }[s] || "bg-zinc-500/20 text-zinc-400");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Route className="w-5 h-5 text-primary" />
                Trip Planner
              </h1>
              <p className="text-xs text-muted-foreground">{trips.length} trips planned</p>
            </div>
          </div>
          <Button onClick={handleCreate} data-testid="add-trip-btn">
            <Plus className="w-4 h-4 mr-2" /> New Trip
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {trips.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Route className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No trips planned yet</p>
            <p className="text-sm">Click "New Trip" to start planning your next adventure.</p>
          </div>
        )}

        {trips.map((trip) => (
          <Card key={trip.id} data-testid={`trip-${trip.id}`}>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-base">{trip.name}</h3>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    {trip.trip_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(trip.trip_date + "T00:00:00").toLocaleDateString()}
                      </span>
                    )}
                    {trip.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {trip.location}
                      </span>
                    )}
                    {trip.trail_name && (
                      <span className="flex items-center gap-1">
                        <Route className="w-3 h-3" /> {trip.trail_name}
                      </span>
                    )}
                  </div>
                </div>
                <Badge className={`text-xs ${statusColor(trip.status)}`}>{trip.status}</Badge>
              </div>

              {/* Vehicles */}
              {trip.vehicles?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {trip.vehicles.map((v) => (
                    <Badge key={v.id} variant="outline" className="text-xs">
                      <Truck className="w-3 h-3 mr-1" /> {v.nickname}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Checklist summary */}
              {trip.checklist?.length > 0 && (
                <div className="text-xs text-muted-foreground mb-2">
                  <CheckSquare className="w-3 h-3 inline mr-1" />
                  {trip.checklist.filter((c) => c.done).length}/{trip.checklist.length} items packed
                </div>
              )}

              {/* Weather badge */}
              {weatherData[trip.id]?.trip_day_forecast && (
                <button
                  onClick={() => setWeatherDetail(weatherData[trip.id])}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-sky-50 border border-sky-200 text-xs mb-2 hover:bg-sky-100 transition-colors w-fit"
                  data-testid={`weather-badge-${trip.id}`}
                >
                  <WeatherIcon icon={weatherData[trip.id].trip_day_forecast.icon} className="w-4 h-4 text-sky-600" />
                  <span className="font-medium text-sky-800">
                    {Math.round(weatherData[trip.id].trip_day_forecast.temp_high)}°/{Math.round(weatherData[trip.id].trip_day_forecast.temp_low)}°F
                  </span>
                  <span className="text-sky-600">{weatherData[trip.id].trip_day_forecast.condition}</span>
                  {weatherData[trip.id].trip_day_forecast.precip_chance > 0 && (
                    <span className="flex items-center gap-0.5 text-sky-600">
                      <Droplets className="w-3 h-3" />{weatherData[trip.id].trip_day_forecast.precip_chance}%
                    </span>
                  )}
                </button>
              )}
              {weatherData[trip.id] && !weatherData[trip.id].trip_day_forecast && (
                <button
                  onClick={() => setWeatherDetail(weatherData[trip.id])}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-zinc-50 border border-zinc-200 text-xs mb-2 hover:bg-zinc-100 transition-colors w-fit text-muted-foreground"
                  data-testid={`weather-badge-${trip.id}`}
                >
                  <Cloud className="w-3.5 h-3.5" />
                  7-day forecast available — tap to view
                </button>
              )}
              {loadingWeather[trip.id] && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading weather...
                </div>
              )}

              {/* Trail hazard alerts */}
              {weatherData[trip.id]?.alerts?.length > 0 && (
                <div className="space-y-1 mb-2">
                  {weatherData[trip.id].alerts
                    .filter((a) => a.is_trip_day || a.severity === "critical" || a.severity === "warning")
                    .slice(0, 3)
                    .map((alert, ai) => (
                    <button
                      key={`${alert.date}-${alert.type}`}
                      onClick={() => setWeatherDetail(weatherData[trip.id])}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs w-full text-left transition-colors ${
                        alert.severity === "critical"
                          ? "bg-red-50 border border-red-300 text-red-800 hover:bg-red-100"
                          : alert.severity === "warning"
                          ? "bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
                          : "bg-yellow-50 border border-yellow-200 text-yellow-800 hover:bg-yellow-100"
                      }`}
                      data-testid={`alert-${trip.id}-${ai}`}
                    >
                      <AlertIcon type={alert.type} className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-medium">{alert.title}</span>
                      <span className="text-muted-foreground ml-auto flex-shrink-0">{alert.date.slice(5)}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setEditTrip({...trip})} data-testid={`edit-trip-${trip.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleGenerateChecklist(trip.id)}
                  disabled={generatingChecklist === trip.id}
                  data-testid={`gear-checklist-${trip.id}`}
                  className="text-primary border-primary/30"
                >
                  {generatingChecklist === trip.id ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                  )}
                  Gear List
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(trip.id)} className="ml-auto text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </main>

      {/* Edit Trip Dialog */}
      <Dialog open={!!editTrip} onOpenChange={(open) => !open && setEditTrip(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Trip</DialogTitle>
            <p className="text-sm text-muted-foreground">Update trip details, vehicles, and packing checklist.</p>
          </DialogHeader>
          {editTrip && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Trip Name</Label>
                <Input value={editTrip.name} onChange={(e) => setEditTrip({...editTrip, name: e.target.value})} data-testid="trip-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={editTrip.trip_date || ""} onChange={(e) => setEditTrip({...editTrip, trip_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={editTrip.status}
                    onChange={(e) => setEditTrip({...editTrip, status: e.target.value})}
                  >
                    <option value="planned">Planned</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Trail Name</Label>
                <Input value={editTrip.trail_name || ""} onChange={(e) => setEditTrip({...editTrip, trail_name: e.target.value})} placeholder="e.g., Rubicon Trail" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={editTrip.location || ""} onChange={(e) => setEditTrip({...editTrip, location: e.target.value})} placeholder="e.g., Moab, UT" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={editTrip.notes || ""} onChange={(e) => setEditTrip({...editTrip, notes: e.target.value})} rows={2} />
              </div>

              {/* Vehicle Selection */}
              <div className="space-y-2">
                <Label>Vehicles on This Trip</Label>
                <div className="space-y-1.5">
                  {vehicles.map((v) => (
                    <div key={v.id} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                      <Checkbox
                        checked={(editTrip.vehicle_ids || []).includes(v.id)}
                        onCheckedChange={() => toggleVehicle(v.id)}
                        data-testid={`trip-vehicle-${v.id}`}
                      />
                      <Truck className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm">{v.nickname} ({v.year} {v.make})</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Checklist */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Packing Checklist</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addChecklistItem} data-testid="add-checklist-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {(editTrip.checklist || []).map((item, i) => (
                    <div key={item._key || `cl-${i}`} className="flex items-center gap-2" data-testid={`checklist-row-${i}`}>
                      <Checkbox
                        checked={item.done}
                        onCheckedChange={(v) => updateChecklistItem(i, "done", v)}
                      />
                      <Input
                        value={item.text}
                        onChange={(e) => updateChecklistItem(i, "text", e.target.value)}
                        placeholder="e.g., Recovery strap, First aid kit"
                        className="flex-1 h-8 text-sm"
                        data-testid={`checklist-input-${i}`}
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeChecklistItem(i)} className="px-1" data-testid={`remove-checklist-${i}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTrip(null)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-trip-btn">Save Trip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Trip?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gear Checklist Dialog */}
      <Dialog open={!!gearChecklist} onOpenChange={(open) => !open && setGearChecklist(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Gear Checklist
            </DialogTitle>
            <DialogDescription>
              {gearChecklist?.trip_name}
              {gearChecklist?.location ? ` — ${gearChecklist.location}` : ""}
            </DialogDescription>
          </DialogHeader>
          {gearChecklist && (
            <div className="space-y-4 py-2">
              {(gearChecklist.checklist?.categories || []).map((cat) => (
                <div key={cat.name}>
                  <h3 className="text-sm font-bold mb-2 text-primary">{cat.name}</h3>
                  <div className="space-y-1">
                    {(cat.items || []).map((item) => (
                      <div key={`${cat.name}-${item}`} className="flex items-center gap-2 py-1 px-2 rounded bg-muted/30 text-sm">
                        <div className="w-3.5 h-3.5 border border-zinc-400 rounded-sm flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleAddChecklistToTrip} data-testid="add-to-trip-btn">
              <Plus className="w-4 h-4 mr-1" /> Add to Trip Checklist
            </Button>
            <Button onClick={handlePrintChecklist} data-testid="print-checklist-btn">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weather Detail Dialog */}
      <Dialog open={!!weatherDetail} onOpenChange={(open) => !open && setWeatherDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-sky-500" />
              7-Day Forecast
            </DialogTitle>
            <DialogDescription>
              {weatherDetail?.location}
            </DialogDescription>
          </DialogHeader>
          {weatherDetail && (
            <div className="space-y-2 py-2">
              {weatherDetail.forecast.map((day) => {
                const isTripDay = day.date === weatherDetail.trip_date;
                return (
                  <div
                    key={day.date}
                    className={`flex items-center gap-3 p-2.5 rounded-md text-sm ${
                      isTripDay ? "bg-sky-50 border border-sky-200 ring-1 ring-sky-300" : "bg-muted/30"
                    }`}
                    data-testid={`forecast-${day.date}`}
                  >
                    <div className="w-10 text-center flex-shrink-0">
                      <div className="font-bold text-xs">{day.day_name}</div>
                      <div className="text-xs text-muted-foreground">{day.date.slice(5)}</div>
                    </div>
                    <WeatherIcon icon={day.icon} className={`w-5 h-5 flex-shrink-0 ${isTripDay ? "text-sky-600" : "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs truncate">{day.condition}</div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                      <span className="flex items-center gap-0.5">
                        <Thermometer className="w-3 h-3" />
                        <span className="font-bold">{Math.round(day.temp_high)}°</span>
                        <span className="text-muted-foreground">/{Math.round(day.temp_low)}°</span>
                      </span>
                      {day.precip_chance > 0 && (
                        <span className="flex items-center gap-0.5 text-sky-600">
                          <Droplets className="w-3 h-3" />{day.precip_chance}%
                        </span>
                      )}
                      <span className="flex items-center gap-0.5 text-muted-foreground">
                        <Wind className="w-3 h-3" />{Math.round(day.wind_max)}
                      </span>
                    </div>
                    {isTripDay && (
                      <Badge className="bg-sky-500/20 text-sky-700 border-sky-500/30 text-xs flex-shrink-0">
                        Trip Day
                      </Badge>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-muted-foreground text-center pt-2">
                Temps in °F · Wind in mph · Powered by Open-Meteo
              </p>

              {/* Trail Hazard Alerts */}
              {weatherDetail.alerts?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border space-y-2">
                  <h3 className="text-sm font-bold flex items-center gap-1.5">
                    <ShieldAlert className="w-4 h-4 text-amber-500" />
                    Trail Hazard Alerts ({weatherDetail.alerts.length})
                  </h3>
                  {weatherDetail.alerts.map((alert) => (
                    <div
                      key={`${alert.date}-${alert.type}`}
                      className={`p-3 rounded-md text-sm ${
                        alert.severity === "critical"
                          ? "bg-red-50 border border-red-300"
                          : alert.severity === "warning"
                          ? "bg-amber-50 border border-amber-300"
                          : "bg-yellow-50 border border-yellow-200"
                      }`}
                      data-testid={`alert-detail-${ai}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <AlertIcon
                          type={alert.type}
                          className={`w-4 h-4 flex-shrink-0 ${
                            alert.severity === "critical" ? "text-red-600" : alert.severity === "warning" ? "text-amber-600" : "text-yellow-600"
                          }`}
                        />
                        <span className="font-bold text-xs">{alert.title}</span>
                        {alert.is_trip_day && (
                          <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-xs ml-auto">
                            Trip Day
                          </Badge>
                        )}
                        {!alert.is_trip_day && (
                          <span className="text-xs text-muted-foreground ml-auto">{alert.date.slice(5)}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{alert.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
